// Client wrapper for the candidate self-verify endpoints on the api service
// (served same-origin at /api/verify/*). Mirrors lib/teamApi.ts: thin fetch
// helpers that forward the PocketBase session token where the server needs to
// authorize the caller.
//
//   sendVerifyRequests  recruiter+ — email self-verify links to candidates.
//   verifyInfo/startPhone/confirmPhone  public — used by the unauthenticated
//   /self-verify page; they carry no token, only the link's verify token.
//
// Completed verifications stream back into the console over the store's realtime
// candidates subscription, so the recruiter side never polls these.

import { startRegistration } from '@simplewebauthn/browser'
import { onAuthExpired } from './pb'

const BASE = '/api/verify'

async function request<T>(
  method: 'GET' | 'POST',
  path: string,
  opts: { body?: unknown; pbToken?: string; query?: Record<string, string> } = {},
): Promise<T> {
  const qs = opts.query ? `?${new URLSearchParams(opts.query)}` : ''
  const res = await fetch(`${BASE}${path}${qs}`, {
    method,
    headers: {
      ...(method === 'POST' ? { 'Content-Type': 'application/json' } : {}),
      ...(opts.pbToken ? { 'X-PB-Token': opts.pbToken } : {}),
    },
    body: method === 'POST' ? JSON.stringify(opts.body ?? {}) : undefined,
  })
  const data = await res.json().catch(() => ({}))
  if (res.status === 401) onAuthExpired()
  if (!res.ok) throw new Error((data as { error?: string })?.error || 'Verification request failed.')
  return data as T
}

export interface SendResult {
  ok: true
  results: { id: string; ok: boolean; emailDelivered?: boolean; error?: string }[]
}

/** Recruiter+: email a secure self-verify link to each candidate. The candidate
 *  state flips back to the console over realtime, so this doesn't return rows. */
export function sendVerifyRequests(ids: string[], pbToken: string): Promise<SendResult> {
  return request('POST', '/send', { body: { ids }, pbToken })
}

export interface VerifyInfo {
  name: string // candidate's first name
  step: string // signal id being advanced, e.g. "phone"
  stepLabel: string // "Verify your phone number"
  proves: string // what completing it establishes
  expires: string
  company: string // who invited them (denormalized), e.g. "Northwind"
  holderBound: boolean // a web holder key is already bound (resumed link)
  custody: 'none' | 'web' | 'device' // current holder-key custody on the row
}

/** Public: look up a pending self-verify request by its token (the page load). */
export function verifyInfo(token: string): Promise<VerifyInfo> {
  return request('GET', '/info', { query: { token } })
}

/** Public: bind a web holder key (a WebAuthn passkey) to this candidate. The
 *  passkey's public key becomes the `cnf` for any web-minted credential; it flips
 *  the `web_custody` signal and marks the row custody:'web' (caps at Tier 2). */
export async function bindHolderKey(token: string): Promise<void> {
  const options = await request<Parameters<typeof startRegistration>[0]['optionsJSON']>('POST', '/passkey/begin', {
    body: { token },
  })
  const response = await startRegistration({ optionsJSON: options })
  await request('POST', '/passkey/finish', { body: { token, response } })
}

/** Public: run the Telesign risk assessment + send an SMS code to `phone`. */
export function startPhone(
  token: string,
  phone: string,
): Promise<{ ok: true; sent: true; delivered: boolean; maskedPhone: string }> {
  return request('POST', '/start-phone', { body: { token, phone } })
}

/** Public: submit the SMS code → record the phone proof + risk on the candidate. */
export function confirmPhone(
  token: string,
  code: string,
): Promise<{ ok: true; tier: number; state: string }> {
  return request('POST', '/confirm-phone', { body: { token, code } })
}
